import axios from "axios";
import { createLogger } from "debug/DebugingLogger";
import qs from "qs";

async function calculateChecksumBuffer(buffer: ArrayBuffer): Promise<string> {
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}
import { dataServiceApi, axiosInstance } from "contexts/axiosApiInstance";
import { uploadGoogleSheet } from "Worker/uploadFile";
const log = createLogger("spreadsheetApi");
interface SheetInfo {
  name: string;
  gid: number;
}

interface FetchSpreadsheetResponse {
  spreadsheet_id: string;
  sheets: SheetInfo[];
}
interface FetchSheetResponse {
  sheet_data: string[][];
}

interface SheetIngestionResponse {
  table_id: string;
  connection_id: string;
  // Parsed from JSON (`response.json()`), so these are plain objects, not Maps.
  column_mapping: Record<string, string>[];
}

interface SheetDetails {
  spreadsheet_id: string;
  spreadsheet_name: string;
  sheet_name: string;
  sheet_data: string[][];
  range: string | null;
}

interface CheckForUpdatesResponse {
  has_updates: boolean;
  fetched_data: { data: string[][] };
  fetched_metadata: SheetMetadata;
  stored_columns: Record<string, string>[];
}

interface SheetMetadata {
  spreadsheet_id: string;
  sheet_name: string;
  range: string | null;
}
export interface SyncUpdatesRequest {
  model_id: string;
  table_id: string;
  fetched_metadata: SheetMetadata;
  connection_id: string;
  column_mapping: Record<string, string>[];
  table_version: number;
}

/// Matches the JSON serialization of `common_core::source::sources::GoogleSheet`.
/// The wasm-bindgen `GoogleSheet` class uses `_str` getters for JS but the
/// server response uses raw serde field names — keep them distinct.
export interface SyncedGoogleSheetSource {
  connection_id_str: string;
  column_mapping_str: Array<{ id: string; name: string | null }>;
  /** v1.2.3+ drilldown identity. Optional — pre-v1.2.3 servers omit. */
  saved_connection_id?: string;
  spreadsheet_id?: string;
  sheet_name?: string;
}

export interface SyncSheetResponse {
  updated_snapshot: string | null;
  updated_source: SyncedGoogleSheetSource;
  schema_update: boolean;
  meta_update: boolean;
  data_version?: number;
}

export const fetchSpreadsheet = async (
  spreadsheetUrl: string,
  savedConnectionId: string,
): Promise<FetchSpreadsheetResponse> => {
  const encodedUrl = encodeURIComponent(spreadsheetUrl);

  try {
    const response = await axiosInstance.get<FetchSpreadsheetResponse>(
      `/action/sheet/${encodedUrl}`,
      { params: { saved_connection_id: savedConnectionId } },
    );
    log.info("Spreadsheet details fetched successfully");
    return response.data;
  } catch (error: unknown) {
    const serverMessage = axios.isAxiosError(error)
      ? (error.response?.data?.message as string)
      : undefined;
    log.error("Failed to fetch spreadsheet details:%o", error);
    throw new Error(serverMessage || "Failed to fetch spreadsheet");
  }
};

export const fetchSheet = async (
  spreadsheetId: string,
  sheetName: string,
  range: string = "",
  savedConnectionId: string,
): Promise<FetchSheetResponse> => {
  try {
    const rangeParam = range ? `/${range}` : "/";

    const response = await axiosInstance.get<FetchSheetResponse>(
      `/action/fetch_gsheet/${spreadsheetId}/${sheetName}${rangeParam}`,
      { params: { saved_connection_id: savedConnectionId } },
    );
    return response.data;
  } catch (error) {
    log.error("Error fetching sheet data:%o", error);
    throw new Error("Failed to fetch sheet data");
  }
};

export const processSheetData2 = async (
  sheetDetails: SheetDetails,
  modelId: string,
  savedConnectionId: string,
): Promise<SheetIngestionResponse> => {
  const dataString = JSON.stringify(sheetDetails.sheet_data);
  const dataBlob = new Blob([dataString], { type: "application/octet-stream" });

  const url = new URL(
    `tables/gsheet/upload`,
    import.meta.env.VITE_APP_DATA_SERVICE,
  );

  return uploadGoogleSheet(
    dataBlob,
    sheetDetails.sheet_name,
    sheetDetails.spreadsheet_name,
    modelId,
    sheetDetails.spreadsheet_id,
    sheetDetails.range ?? "",
    url,
    savedConnectionId,
  )
    .then(async (response) => {
      if (response.status === 201) {
        return response.json() as Promise<SheetIngestionResponse>;
      }
      throw new Error(`Upload failed with status: ${response.status}`);
    })
    .catch((error: unknown) => {
      const message = error instanceof Error ? error.message : "Upload failed";
      console.error("GSheet Upload Error:", message);
      throw error;
    });
};

export const checkForUpdates = async (
  connectionId: string,
  tableVersion: number,
  tableId: string,
  savedConnectionId: string,
): Promise<CheckForUpdatesResponse> => {
  try {
    const response = await axiosInstance.get<CheckForUpdatesResponse>(
      `/action/check_updates`,
      {
        params: {
          connection_id: connectionId,
          table_version: tableVersion,
          table_id: tableId,
          saved_connection_id: savedConnectionId,
        },
      },
    );
    log.info("Checked updates :", response.data);
    return response.data;
  } catch (error) {
    console.error("Failed to check updates:", error);
    throw new Error("Failed to check updates");
  }
};

export const syncUpdates = async (
  syncUpdatesRequest: SyncUpdatesRequest,
  sheetData: string[][],
): Promise<SyncSheetResponse> => {
  const dataString = JSON.stringify(sheetData);
  const dataBlob = new Blob([dataString], { type: "application/octet-stream" });
  const arrayBuffer = await dataBlob.arrayBuffer();
  const checksum = await calculateChecksumBuffer(arrayBuffer);
  const queryString = qs.stringify(syncUpdatesRequest, { encode: false });

  try {
    const response = await dataServiceApi.post(
      `/tables/sync?${queryString}`,
      dataBlob,
      {
        headers: {
          "Content-Type": "application/octet-stream",
          "X-Checksum": checksum,
        },
      },
    );

    return response.data as SyncSheetResponse;
  } catch (error: unknown) {
    const errorResponse = {
      status: axios.isAxiosError(error) ? error.response?.status || 500 : 500,
      message: axios.isAxiosError(error)
        ? (error.response?.data as string) ||
          "Network error or server not responding"
        : "Network error or server not responding",
      originalError: error,
    };

    throw errorResponse;
  }
};
